import { useEffect, useState } from "react";
import axios from "axios";
import { Toaster } from "sonner";
import { ArrowLeft, Clock, MapPin, MessageCircle, Palmtree, Users, Waves } from "lucide-react";
import { AdaptiveGallery, ShareBar, shareLink } from "@/components/ShareGallery";
import { waLink, BRAND } from "@/lib/site";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { TourModal, durationLabel } from "@/components/ToursSection";
import { BRL } from "@/components/PixPayment";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TourPage({ id }) {
  const [tour, setTour] = useState(undefined);
  const [galleryAt, setGalleryAt] = useState(null);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/tours`).then((r) => {
      const t = (r.data || []).find((x) => x.id === id) || null;
      setTour(t);
      if (t) document.title = `${t.title} — ${BRAND}`;
    }).catch(() => setTour(null));
  }, [id]);

  const wa = tour && waLink(tour.whatsapp, `Olá, equipe Se Hospede AKI! Tenho interesse no passeio de jet ski "${tour.title}" e gostaria de tirar algumas dúvidas antes de reservar:\n\n• Horários disponíveis na minha data\n• Quantas pessoas podem ir por jet ski\n• Ponto de encontro e o que está incluso\n\nAguardo o retorno. Obrigado(a)!`);

  return (
    <div className="min-h-screen bg-[#FBF8F3] text-slate-800" style={{ fontFamily: "Outfit, system-ui, sans-serif" }}>
      <Toaster position="top-center" richColors />
      <header className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <span className="bg-[#0A66C2] p-1.5 rounded-lg"><Palmtree className="text-white w-5 h-5" /></span>
            <span className="text-lg font-extrabold text-[#0A66C2] whitespace-nowrap">Se Hospede <span className="text-[#FFB800]">AKI</span></span>
          </a>
          <a href="/#passeios" className="text-sm font-semibold text-slate-500 hover:text-[#0A66C2] flex items-center gap-1" data-testid="tour-page-back"><ArrowLeft size={14} /> Todos os passeios</a>
        </div>
      </header>

      {tour === undefined && <p className="text-center py-24 text-slate-400">Carregando...</p>}
      {tour === null && (
        <div className="text-center py-24" data-testid="tour-not-found">
          <p className="text-xl font-bold mb-2">Passeio não encontrado</p>
          <a href="/#passeios" className="text-[#0A66C2] font-semibold">Ver passeios disponíveis</a>
        </div>
      )}
      {tour && (
        <main className="max-w-6xl mx-auto px-4 py-8 sm:py-12" data-testid="tour-page">
          <AdaptiveGallery images={tour.images || []} title={tour.title} onOpen={setGalleryAt} testId="tour-page-gallery" />
          {galleryAt !== null && <PhotoLightbox images={tour.images} title={tour.title} start={galleryAt} onClose={() => setGalleryAt(null)} />}

          <div className="grid lg:grid-cols-3 gap-10 mt-10">
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-1.5 bg-cyan-100 text-cyan-800 text-xs font-bold uppercase px-3 py-1 rounded-full mb-4"><Waves size={13} /> Passeio de jet ski</span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-3" style={{ fontFamily: "Fraunces, serif" }}>{tour.title}</h1>
              <p className="text-slate-500 flex items-center gap-1.5 mb-6"><MapPin size={16} className="text-[#0A66C2]" /> {tour.location}</p>
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm flex items-center gap-2"><Clock size={16} className="text-[#0A66C2]" /> {durationLabel(tour.duration_min)}</span>
                <span className="bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm flex items-center gap-2"><Users size={16} className="text-[#0A66C2]" /> até {tour.max_people_per_unit} por jet ski</span>
                <span className="bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm">Horários: {tour.time_slots.join(" · ")}</span>
              </div>
              {tour.description && <p className="text-base sm:text-lg text-slate-600 leading-relaxed whitespace-pre-line mb-6">{tour.description}</p>}
              {tour.meeting_point && <p className="text-sm text-slate-500 mb-8"><strong className="text-slate-700">Ponto de encontro:</strong> {tour.meeting_point}</p>}
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3">Divulgue este passeio</h2>
              <ShareBar shareUrl={shareLink("passeio", tour.id)} text={`${tour.title} — ${BRL(tour.price)} ${tour.price_unit}. Reserve pelo ${BRAND}:`} testPrefix="tour" />
            </div>
            <aside className="lg:sticky lg:top-24 h-fit bg-white rounded-[2rem] border border-slate-200 shadow-sm p-6" data-testid="tour-page-booking-card">
              <p className="text-slate-400 text-xs font-bold uppercase">{tour.price_unit}</p>
              <p className="text-4xl font-extrabold text-[#0A66C2] mb-1">{BRL(tour.price)}</p>
              <p className="text-sm text-slate-500 mb-6">Sinal de {tour.deposit_percent}% via PIX · restante no dia</p>
              <button onClick={() => setBooking(true)} className="w-full bg-[#FFCC00] text-blue-900 py-4 rounded-2xl font-extrabold text-lg shadow-[0_4px_0_#b38f00] active:translate-y-1 active:shadow-none transition-transform mb-3" data-testid="tour-page-book">
                Reservar passeio
              </button>
              {wa && <a href={wa} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-[#25D366] text-[#128C4A] font-bold" data-testid="tour-page-whatsapp"><MessageCircle size={18} /> Tirar dúvidas no WhatsApp</a>}
            </aside>
          </div>
        </main>
      )}
      {booking && tour && <TourModal tour={tour} onClose={() => setBooking(false)} />}
    </div>
  );
}
